import type { Dayjs } from 'dayjs';
import type { TimePickerProps } from '@mui/x-date-pickers/TimePicker';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import type { DateTimePickerProps } from '@mui/x-date-pickers/DateTimePicker';
import type { PickersTextFieldProps } from '@mui/x-date-pickers/PickersTextField';

import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

// ----------------------------------------------------------------------

type DateInput = Dayjs | Date | string | number | null | undefined;

function normalizeDateValue(value: DateInput): Dayjs | null {
  if (dayjs.isDayjs(value)) return value;

  const parsed = value ? dayjs(value) : null;
  return parsed?.isValid() ? parsed : null;
}

// ----------------------------------------------------------------------

type PickerProps<T extends DatePickerProps | TimePickerProps | DateTimePickerProps> = T & {
  name: string;
  slotProps?: T['slotProps'] & {
    textField?: Partial<PickersTextFieldProps>;
  };
};

export function RHFDatePicker({ name, slotProps, ...other }: PickerProps<DatePickerProps>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <DatePicker
          {...field}
          value={normalizeDateValue(field.value)}
          onChange={(newValue) => {
            if (!newValue) {
              field.onChange(null);
              return;
            }

            const parsedValue = dayjs(newValue);
            field.onChange(parsedValue.isValid() ? parsedValue.format() : newValue);
          }}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              error: !!error,
              helperText: error?.message ?? slotProps?.textField?.helperText,
            },
          }}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFTimePicker({ name, slotProps, ...other }: PickerProps<TimePickerProps>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TimePicker
          {...field}
          value={normalizeDateValue(field.value)}
          onChange={(newValue) => {
            if (!newValue) {
              field.onChange(null);
              return;
            }

            const parsedValue = dayjs(newValue);
            field.onChange(parsedValue.isValid() ? parsedValue.format() : newValue);
          }}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              error: !!error,
              helperText: error?.message ?? slotProps?.textField?.helperText,
            },
          }}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

export function RHFDateTimePicker({ name, slotProps, ...other }: PickerProps<DateTimePickerProps>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const val = normalizeDateValue(field.value);

        return (
          <DateTimePicker
            {...field}
            value={val}
            referenceDate={dayjs().hour(19).minute(0).second(0)}
            onChange={(newValue) => {
              if (!newValue) {
                field.onChange(null);
                return;
              }

              let parsedValue = dayjs(newValue);
              if (parsedValue.isValid()) {
                const oldDate = val && val.isValid() ? val : null;
                if (!oldDate || !parsedValue.isSame(oldDate, 'day')) {
                  parsedValue = parsedValue.hour(19).minute(0).second(0);
                }
                field.onChange(parsedValue.format());
              } else {
                field.onChange(newValue);
              }
            }}
            slotProps={{
              ...slotProps,
              textField: {
                ...slotProps?.textField,
                error: !!error,
                helperText: error?.message ?? slotProps?.textField?.helperText,
              },
              desktopPaper: {
                sx: {
                  borderRadius: 2.5,
                  boxShadow: '0 20px 40px -4px rgba(0, 0, 0, 0.16), 0 8px 16px -4px rgba(0, 0, 0, 0.08)',
                  border: (theme) => `1.5px solid ${theme.vars.palette.primary.light}`,
                  p: 1,
                  minWidth: 540,
                  '& .MuiPickersLayout-contentWrapper': {
                    gap: 2.5,
                    p: 2,
                  },
                  '& .MuiDateCalendar-root': {
                    width: 340,
                    minWidth: 340,
                    height: 350,
                    maxHeight: 360,
                  },
                  '& .MuiPickersCalendarHeader-root': {
                    pl: 1.5,
                    pr: 1,
                    mb: 1.5,
                    bgcolor: (theme) => `rgba(99, 102, 241, 0.08)`,
                    borderRadius: 1.5,
                    maxHeight: 48,
                    minHeight: 48,
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: 'primary.main',
                    textTransform: 'capitalize',
                  },
                  '& .MuiDayCalendar-header': {
                    justifyContent: 'space-around',
                    mb: 0.5,
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    width: 40,
                    height: 40,
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: 'primary.dark',
                  },
                  '& .MuiPickersDay-root': {
                    width: 40,
                    height: 40,
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    borderRadius: 1.25,
                    m: '2px',
                    '&:hover': {
                      bgcolor: 'rgba(99, 102, 241, 0.12)',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: '#ffffff',
                      fontWeight: 700,
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                    },
                    '&.MuiPickersDay-today': {
                      border: (theme) => `2px solid ${theme.vars.palette.primary.main}`,
                      fontWeight: 700,
                    },
                  },
                  '& .MuiMultiSectionDigitalClock-root': {
                    borderLeft: (theme) => `1.5px solid ${theme.vars.palette.divider}`,
                    pl: 2,
                    pr: 1,
                    maxHeight: 350,
                    bgcolor: 'rgba(99, 102, 241, 0.03)',
                    borderRadius: 1.5,
                  },
                  '& .MuiMultiSectionDigitalClockSection-root': {
                    width: 64,
                    '& .MuiMenuItem-root': {
                      borderRadius: 1.25,
                      my: '4px',
                      mx: '2px',
                      py: '8px',
                      px: '10px',
                      justifyContent: 'center',
                      fontSize: '0.9375rem',
                      fontWeight: 600,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: '#ffffff',
                        fontWeight: 700,
                        boxShadow: '0 3px 10px rgba(99, 102, 241, 0.35)',
                      },
                    },
                  },
                  '& .MuiPickersActionBar-root': {
                    py: 2,
                    px: 2.5,
                    borderTop: (theme) => `1.5px solid ${theme.vars.palette.divider}`,
                    bgcolor: 'rgba(99, 102, 241, 0.02)',
                  },
                  ...(typeof slotProps?.desktopPaper === 'function'
                    ? undefined
                    : slotProps?.desktopPaper?.sx),
                },
              },
            }}
            {...other}
          />
        );
      }}
    />
  );
}
